import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";

const RESOURCE_LABELS = {
  students: "estudiantes",
  teachers: "docentes",
  headquarters: "sedes",
};

// Mensaje según el resultado que el backend anticipa (preview.result).
const RESULT_INFO = {
  trial: {
    variant: "success",
    text: "Se activará con 21 días de prueba gratis, sin cobro inmediato.",
  },
  pending_payment: {
    variant: "info",
    text: "Generaremos una factura. El cambio se activa cuando confirmemos tu pago.",
  },
  scheduled: {
    variant: "warning",
    text: "Bajarás a este plan al terminar tu período actual (no pierdes lo ya pagado).",
  },
  applied: {
    variant: "secondary",
    text: "El cambio se aplicará de inmediato.",
  },
};

/**
 * PlanChangeModal — confirma un cambio de plan y, si es un downgrade que deja
 * recursos por encima del nuevo límite, actúa como wizard para elegir cuáles
 * quedan activos (los demás se desactivan, sin borrarse).
 *
 * Props:
 *   show, onHide, loading
 *   plan     — plan destino { id, name }
 *   preview  — { result, requires_selection, overages, features_lost }
 *   onConfirm(keep|undefined)
 */
function PlanChangeModal({ show, onHide, loading, plan, preview, onConfirm }) {
  const [keep, setKeep] = useState({});

  const requiresSelection = preview?.requires_selection;
  const overages = preview?.overages ?? {};
  const featuresLost = preview?.features_lost ?? [];

  // Preselecciona los primeros `limit` de cada recurso al abrir.
  useEffect(() => {
    if (requiresSelection) {
      const init = {};
      for (const [key, ov] of Object.entries(overages)) {
        init[key] = ov.items.slice(0, ov.limit).map((it) => it.id);
      }
      setKeep(init);
    } else {
      setKeep({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, preview]);

  const toggle = (key, id, limit) => {
    setKeep((prev) => {
      const cur = prev[key] ?? [];
      if (cur.includes(id)) return { ...prev, [key]: cur.filter((x) => x !== id) };
      if (cur.length >= limit) return prev; // ya llegó al máximo del nuevo plan
      return { ...prev, [key]: [...cur, id] };
    });
  };

  const info = RESULT_INFO[preview?.result] ?? RESULT_INFO.applied;

  const handleConfirm = () => onConfirm(requiresSelection ? keep : undefined);

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Cambiar a plan {plan?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant={info.variant} className="py-2">
          {info.text}
        </Alert>

        {featuresLost.length > 0 && (
          <div className="mb-3">
            <div className="fw-medium mb-1">
              <i className="mdi mdi-alert-outline text-warning me-1" aria-hidden="true" />
              Dejarás de tener:
            </div>
            <ul className="mb-0 small text-muted">
              {featuresLost.map((f) => (
                <li key={f.key}>{f.label}</li>
              ))}
            </ul>
          </div>
        )}

        {requiresSelection &&
          Object.entries(overages).map(([key, ov]) => {
            const selected = keep[key] ?? [];
            return (
              <div key={key} className="mb-3">
                <div className="fw-medium">
                  {RESOURCE_LABELS[key] ?? key}{" "}
                  <Badge bg={selected.length > ov.limit ? "danger" : "primary"}>
                    {selected.length}/{ov.limit}
                  </Badge>
                </div>
                <div className="text-muted small mb-2">
                  Tienes {ov.current} activos y el plan {plan?.name} permite {ov.limit}.
                  Elige cuáles quedan activos; el resto se desactivará (no se borra).
                </div>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: 200, overflowY: "auto" }}
                >
                  {ov.items.map((it) => {
                    const checked = selected.includes(it.id);
                    const atCap = !checked && selected.length >= ov.limit;
                    return (
                      <Form.Check
                        key={it.id}
                        type="checkbox"
                        id={`keep-${key}-${it.id}`}
                        label={it.label}
                        checked={checked}
                        disabled={atCap}
                        onChange={() => toggle(key, it.id, ov.limit)}
                        className="mb-1"
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : "Confirmar cambio"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { PlanChangeModal };
